export const SERVICE_PAGES = {
  'bulk-sms-crm': {
    title: 'Bulk SMS CRM',
    eyebrow: 'Twilio bulk SMS alternative',
    metaTitle: 'Bulk SMS CRM for Small Business | Ineedlead CRM',
    metaDescription: 'Send bulk SMS campaigns, manage replies, tags, opt-outs, delivery tracking, and customer conversations from one private CRM workspace.',
    summary: 'Send campaigns to opted-in contacts, track delivery, handle replies, and keep every customer record in one place.',
    keywords: ['bulk SMS CRM', 'Twilio bulk SMS', 'SMS campaign software', 'SMS marketing CRM', 'bulk text messaging'],
    outcomes: ['Import and organize customer lists', 'Send scheduled SMS campaigns', 'Track delivery and replies', 'Keep STOP replies out of future sends'],
    useCases: ['Event updates and reminders', 'Appointment confirmations', 'Flash sales and coupon texts', 'Venue or schedule changes'],
  },
  'twilio-missed-call-text-back': {
    title: 'Missed Call Text Back',
    eyebrow: 'Twilio missed call automation',
    metaTitle: 'Twilio Missed Call Text Back Automation | Ineedlead CRM',
    metaDescription: 'Automatically text callers after a missed call, route inbound calls, log call history, and keep callbacks organized in your CRM.',
    summary: 'When a customer calls and nobody answers, Ineedlead CRM can send a text back, log the call, and help your team follow up.',
    keywords: ['Twilio missed call text back', 'missed call SMS automation', 'missed call callback CRM', 'business call follow up'],
    outcomes: ['Send instant missed-call replies', 'Log inbound and outbound calls', 'Use fallback forwarding', 'Keep call records attached to contacts'],
    useCases: ['After-hours enquiries', 'Busy appointment teams', 'Contractors on job sites', 'Med spa and salon callbacks'],
  },
  'whatsapp-api-crm': {
    title: 'WhatsApp API CRM',
    eyebrow: 'WhatsApp business messaging',
    metaTitle: 'WhatsApp API CRM for Business | Ineedlead CRM',
    metaDescription: 'Connect WhatsApp messaging to your CRM, send customer messages, receive replies, and keep conversations organized.',
    summary: 'Use WhatsApp as part of the same customer workspace instead of switching between separate tabs and phones.',
    keywords: ['WhatsApp API CRM', 'WhatsApp CRM for business', 'WhatsApp business messaging', 'WhatsApp customer inbox'],
    outcomes: ['Send WhatsApp messages from your workspace', 'Receive replies in the inbox', 'Keep WhatsApp history per contact', 'Forward replies to your own systems when needed'],
    useCases: ['Event group links', 'Quote follow-ups', 'Customer support replies', 'Appointment reminders'],
  },
  'ai-automation-builder': {
    title: 'AI Automation Builder',
    eyebrow: 'Customer workflow automation',
    metaTitle: 'AI Automation Builder for SMS, WhatsApp and Calls | Ineedlead CRM',
    metaDescription: 'Build customer workflows for keywords, form submissions, reminders, digests, call menus, SMS, WhatsApp, and email.',
    summary: 'Create workflows from plain English or with the visual builder: keywords, tags, campaigns, reminders, and call routing.',
    keywords: ['AI automation builder', 'SMS automation workflow', 'customer follow up automation', 'keyword SMS automation'],
    outcomes: ['Create keyword replies', 'Import leads from forms', 'Send hourly CRM summaries', 'Route calls with menus'],
    useCases: ['Text DANCE for event links', 'Lead form to welcome message', 'Hourly business digest', 'Follow-up reminders'],
  },
  'ivr-call-routing': {
    title: 'IVR Call Routing',
    eyebrow: 'Phone menu automation',
    metaTitle: 'IVR Call Routing and Twilio Voice Automation | Ineedlead CRM',
    metaDescription: 'Create IVR menus, route inbound calls, send follow-up messages after call choices, and log call activity in your CRM.',
    summary: 'Let callers choose what they need, route them correctly, and send follow-up messages based on the option they selected.',
    keywords: ['Twilio IVR', 'IVR call routing', 'phone menu automation', 'call routing CRM'],
    outcomes: ['Build editable phone menus', 'Route by keypad choice', 'Send follow-up SMS or WhatsApp', 'Log every call and selection'],
    useCases: ['Driving school enquiries', 'Appointment booking menus', 'Sales/support routing', 'Callback request flows'],
  },
  'email-marketing-crm': {
    title: 'Email Marketing CRM',
    eyebrow: 'Email campaigns and tracking',
    metaTitle: 'Email Marketing CRM with Opens, Clicks and Bounces | Ineedlead CRM',
    metaDescription: 'Send email campaigns, use verified domains, track opens, clicks, bounces, and replies in one customer workspace.',
    summary: 'Add email campaigns to the same CRM you use for texts, WhatsApp, calls, and automations.',
    keywords: ['email marketing CRM', 'bulk email campaign tracking', 'email open click tracking', 'business email automation'],
    outcomes: ['Send from verified domains', 'Track opens and clicks', 'Monitor bounces', 'See email replies in the inbox'],
    useCases: ['Newsletters', 'Event promotions', 'Customer reactivation', 'Offer follow-ups'],
  },
  'all-in-one-communication-crm': {
    title: 'All-in-One Communication CRM',
    eyebrow: 'Messaging, calls and automation',
    metaTitle: 'All-in-One Communication CRM | Ineedlead CRM',
    metaDescription: 'Manage SMS, WhatsApp, email, calls, campaigns, missed call text back, IVR, and AI automations from one private workspace.',
    summary: 'Bring customer conversations, campaigns, phone calls, and follow-ups into one login with per-business data isolation.',
    keywords: ['communication CRM', 'SMS WhatsApp calls CRM', 'small business messaging CRM', 'customer communication platform'],
    outcomes: ['One inbox for customer history', 'Feature-based pricing', 'Private workspace per business', 'Owner-controlled access'],
    useCases: ['Local service teams', 'Agencies', 'Med spas', 'Events and community groups'],
  },
};

export const SERVICE_LINKS = Object.entries(SERVICE_PAGES).map(([slug, page]) => ({
  slug,
  title: page.title,
  summary: page.summary,
}));

export const BLOG_POSTS = {
  'twilio-bulk-sms-small-business-guide': {
    title: 'Twilio Bulk SMS for Small Business: What You Need Beyond Sending Texts',
    metaTitle: 'Twilio Bulk SMS for Small Business | Ineedlead CRM',
    metaDescription: 'A practical guide to bulk SMS, opt-ins, STOP handling, delivery tracking, replies, and customer records for small businesses.',
    excerpt: 'Bulk SMS works better when it is connected to contacts, replies, opt-outs, and follow-up workflows.',
    category: 'Bulk SMS',
    readTime: '6 min read',
    sections: [
      ['Sending is only one part of bulk SMS', 'A good SMS setup needs contact organization, tags, delivery status, reply handling, and unsubscribe management. Without those pieces, a campaign can create more manual work than it saves.'],
      ['Keep replies beside the customer record', 'When replies land in the same inbox as contact history, the team can answer faster and avoid sending the same message twice.'],
      ['Use automation carefully', 'Keyword replies, appointment reminders, and campaign follow-ups are useful when they are tied to opt-in status and clear customer intent.'],
    ],
  },
  'missed-call-text-back-automation': {
    title: 'Missed Call Text Back: How Local Businesses Recover More Leads',
    metaTitle: 'Missed Call Text Back Automation | Ineedlead CRM',
    metaDescription: 'Learn how missed call text back automation works for Twilio phone numbers, local services, med spas, and appointment teams.',
    excerpt: 'A fast text after a missed call can turn a lost caller into a booked customer.',
    category: 'Calls',
    readTime: '5 min read',
    sections: [
      ['Speed matters after a missed call', 'Many callers will not leave voicemail. A short automatic text keeps the conversation alive and gives them a simple way to reply.'],
      ['Log the call automatically', 'The best setup records who called, when they called, and whether someone followed up. That makes missed calls visible instead of forgotten.'],
      ['Add routing when the team grows', 'Call menus and fallback forwarding help callers reach the right person without making your process feel complicated.'],
    ],
  },
  'keyword-sms-automation-examples': {
    title: 'Keyword SMS Automation Examples for Events, Classes and Local Promotions',
    metaTitle: 'Keyword SMS Automation Examples | Ineedlead CRM',
    metaDescription: 'Examples of SMS keyword automations like DANCE, HELP, appointment reminders, tags, event links, and welcome messages.',
    excerpt: 'Keywords are simple for customers and easy to automate when the workflow is connected to your CRM.',
    category: 'Automation',
    readTime: '5 min read',
    sections: [
      ['Start with one clear keyword', 'A keyword like DANCE, INFO, BOOK, or VIP should trigger one clear action: save the contact, tag the interest, and send the right reply.'],
      ['Include the next step', 'A strong reply gives the customer a link, a phone number, or a simple question to answer.'],
      ['Keep compliance visible', 'For SMS, include clear unsubscribe instructions where required and always respect opt-out status before sending campaigns.'],
    ],
  },
  'whatsapp-crm-vs-sms-crm': {
    title: 'WhatsApp CRM vs SMS CRM: When to Use Each Channel',
    metaTitle: 'WhatsApp CRM vs SMS CRM | Ineedlead CRM',
    metaDescription: 'Compare WhatsApp CRM and SMS CRM for customer replies, promotions, links, opt-ins, and small business follow-up.',
    excerpt: 'SMS is strong for reach. WhatsApp is strong for conversation. Many businesses need both.',
    category: 'WhatsApp',
    readTime: '4 min read',
    sections: [
      ['SMS reaches quickly', 'Text campaigns are useful for urgent updates, reminders, and simple promotions where reach matters.'],
      ['WhatsApp helps conversations continue', 'WhatsApp is useful when customers need links, richer replies, or ongoing back-and-forth.'],
      ['One customer record matters most', 'The channel is less important than keeping all conversation history attached to one contact.'],
    ],
  },
};

export const BLOG_LINKS = Object.entries(BLOG_POSTS).map(([slug, post]) => ({
  slug,
  title: post.title,
  excerpt: post.excerpt,
  category: post.category,
  readTime: post.readTime,
}));
